import type {
  FormState,
  FormStateValue,
  ReportingBoundFilter,
  ReportingModelDataSourceDeclaration,
  ReportingSequenceBinding,
  ReportingValueSelection,
} from '@de/providers/DataSourceManager.types';
import { hasTemplate, interpolateObject, parse } from '@de/utils/interpolation';
import {
  isPublishedScopeContext,
  isUnpublishedScope,
} from '@de/utils/published-scope/unpublishedScope';
import { readsMissingScopeKey } from '@de/utils/published-scope/scopeReadBy';
import { getSequence, type AnchorMap } from '../../sequences';
import { warnOnce } from '../warnOnce';
import { isDerivedRoot, withDerivedRoots } from './derivedRoots';
import type {
  ReportingOrderClause,
  ReportingQueryFilter,
  ReportingStructuredQueryBody,
} from './reporting-query-types';

/**
 * A HAVING clause with no threshold is a half-authored declaration — the
 * dashboard editor seeds `{ metric, op: 'gte', value: '' }` when an author
 * adds a condition and never fills it in. Reporting rejects the shape, and
 * before it did, it compiled to `agg >= ''`: NULL for every group, so the
 * chart rendered empty with nothing to trace. Dropping the clause keeps
 * saved declarations rendering as their authors intended without a config
 * migration.
 *
 * A clause whose threshold interpolates from an unset token collapses to the
 * same shape, which is why the check runs after interpolation.
 *
 * A list threshold (`op: 'in'`) counts as blank when it is empty *or* when any
 * element is blank — `value: [100, '']` compiles to a comparison against `''`
 * for one branch of the IN, so the clause is no more trustworthy than a wholly
 * blank one and gets dropped rather than silently narrowed to its usable
 * elements.
 */
function isBlankHavingValue(value: unknown): boolean {
  if (Array.isArray(value)) {
    return value.length === 0 || value.some(isBlankHavingValue);
  }
  if (typeof value === 'string') return value.trim().length === 0;
  return value == null;
}

/** Root under which sequence anchors are exposed to templates. */
const SEQUENCES_ROOT = 'sequences';

const LIST_OPS: ReadonlySet<ReportingQueryFilter['op']> = new Set<
  ReportingQueryFilter['op']
>(['in', 'not_in']);

type Allowlist =
  | { readonly mode: 'all' }
  | { readonly mode: 'none' }
  | { readonly mode: 'keys'; readonly keys: ReadonlySet<string> }
  | { readonly mode: 'invalid' };

interface FormEntry {
  readonly key: string;
  readonly value: FormStateValue;
}

/**
 * The form values a declaration reads, in form-state key order: keys the
 * allowlist admits, then `formOverrides` (replacing in place, appending,
 * or removing).
 */
interface ResolvedForm {
  readonly entries: readonly FormEntry[];
  readonly byKey: Readonly<Record<string, FormStateValue>>;
}

/**
 * Build the reporting structured-query body after template interpolation.
 * Shared by fetch and cache-key derivation so both paths stay aligned.
 */
export function interpolateReportingBody(
  decl: ReportingModelDataSourceDeclaration,
  context: unknown,
): ReportingStructuredQueryBody {
  const allow = resolveAllowlist(decl.applicableFormKeys, context);
  const form = resolveForm(decl, readFormState(context), allow);
  const anchorsBySequence = resolveSequenceAnchors(decl.sequenceBindings, form);
  const localContext = withSequenceRoot(decl, context, anchorsBySequence);
  const sequenceByFormKey = indexSequencesByFormKey(decl.sequenceBindings);

  const modelId = String(interpolateObject(decl.modelId, localContext));
  const metrics = selectNames(
    interpolateStringArray(decl.metrics, localContext),
    decl.valueSelections,
    'metrics',
    form,
  );
  const optional = buildOptionalReportingFields(
    decl,
    localContext,
    form,
    anchorsBySequence,
    sequenceByFormKey,
  );

  return {
    modelId,
    metrics,
    ...(decl.dimensions?.length
      ? {
          dimensions: selectNames(
            interpolateStringArray(decl.dimensions, localContext),
            decl.valueSelections,
            'dimensions',
            form,
          ),
        }
      : {}),
    ...optional,
  };
}

/**
 * Roots the declaration derives from the shared context. Today that is
 * `{{sequences.*}}` when it binds a sequence; `undefined` otherwise, so a
 * declaration that binds none interpolates exactly as before.
 */
export function reportingDerivedRoots(
  decl: ReportingModelDataSourceDeclaration,
  context: unknown,
): Readonly<Record<string, unknown>> | undefined {
  if (!decl.sequenceBindings?.length) return undefined;
  const allow = resolveAllowlist(decl.applicableFormKeys, context);
  const form = resolveForm(decl, readFormState(context), allow);
  return {
    [SEQUENCES_ROOT]: resolveSequenceAnchors(decl.sequenceBindings, form),
  };
}

/**
 * A published scope key that the declaration names and nobody published.
 * The fetch stays pending; a request must not carry the sentinel.
 */
export function reportingDeclarationHasUnpublishedScope(
  decl: ReportingModelDataSourceDeclaration,
  context: unknown,
): boolean {
  if (!isPublishedScopeContext(context)) return false;
  const fields = {
    modelId: decl.modelId,
    metrics: decl.metrics,
    dimensions: decl.dimensions,
    segments: decl.segments,
    filters: decl.filters,
    having: decl.having,
    order: decl.order,
    limit: decl.limit,
    context: decl.context,
    applicableFormKeys: decl.applicableFormKeys,
    boundFilters: decl.boundFilters,
    excludeEqFields: decl.excludeEqFields,
    sequenceBindings: decl.sequenceBindings,
    valueSelections: decl.valueSelections,
    formOverrides: decl.formOverrides,
  };
  if (readsMissingScopeKey(fields, publishedScopeBag(context))) return true;
  const resolved = interpolateObject(fields, context);
  return containsUnpublishedScope(resolved);
}

function publishedScopeBag(
  context: unknown,
): Readonly<Record<string, unknown>> {
  if (typeof context !== 'object' || context === null) return {};
  const scope = (context as Record<string, unknown>).scope;
  return typeof scope === 'object' && scope !== null && !Array.isArray(scope)
    ? (scope as Readonly<Record<string, unknown>>)
    : {};
}

/**
 * Stay pending when this declaration names a scope key nobody published,
 * or when its allowlist template resolves to something other than `null`
 * or a list of keys. Sending either would apply the wrong page values.
 */
export function reportingSourceMustWait(
  decl: { readonly kind?: string },
  context: unknown,
): boolean {
  if (decl.kind !== 'reportingModel') return false;
  const reporting = decl as ReportingModelDataSourceDeclaration;
  if (reportingDeclarationHasUnpublishedScope(reporting, context)) return true;
  return (
    resolveAllowlist(reporting.applicableFormKeys, context).mode === 'invalid'
  );
}

/** Bound form keys that should refetch this declaration when they change. */
export function declarationReadsBoundFormKey(
  decl: { readonly kind?: string },
  key: string,
): boolean {
  if (decl.kind !== 'reportingModel') return false;
  return reportingModelReadsFormKey(
    decl as ReportingModelDataSourceDeclaration,
    key,
  );
}

/** Whether `key` feeds this declaration's bound fields. */
export function reportingModelReadsFormKey(
  decl: ReportingModelDataSourceDeclaration,
  key: string,
): boolean {
  if (decl.boundFilters?.some((filter) => filter.formKey === key)) {
    return true;
  }
  if (decl.sequenceBindings?.some((binding) => binding.formKeys.includes(key)))
    return true;
  return (
    decl.valueSelections?.some((selection) =>
      selection.formKeys.includes(key),
    ) ?? false
  );
}

function buildOptionalReportingFields(
  decl: ReportingModelDataSourceDeclaration,
  context: unknown,
  form: ResolvedForm,
  anchorsBySequence: Readonly<Record<string, AnchorMap>>,
  sequenceByFormKey: ReadonlyMap<string, string>,
): Partial<ReportingStructuredQueryBody> {
  const next: {
    segments?: readonly string[];
    filters?: ReportingStructuredQueryBody['filters'];
    having?: ReportingStructuredQueryBody['having'];
    order?: ReportingStructuredQueryBody['order'];
    limit?: number;
    context?: Readonly<Record<string, string | number>>;
  } = {};

  if (decl.segments?.length) {
    next.segments = interpolateStringArray(decl.segments, context);
  }

  const filters = composeFilters(
    decl,
    context,
    form,
    anchorsBySequence,
    sequenceByFormKey,
  );
  if (filters.length > 0) {
    next.filters = filters;
  }

  if (decl.having?.length) {
    const clauses = decl.having
      .map(
        (clause) =>
          interpolateObject(clause, context) as (typeof decl.having)[number],
      )
      .filter((clause) => !isBlankHavingValue(clause?.value));
    if (clauses.length > 0) {
      next.having = clauses;
    }
  }
  if (decl.order?.length) {
    next.order = renameOrder(
      decl.order.map(
        (clause) =>
          interpolateObject(clause, context) as (typeof decl.order)[number],
      ),
      decl.valueSelections,
      form,
    );
  }
  if (decl.limit !== undefined) {
    const limit = interpolateObject(decl.limit, context);
    if (typeof limit === 'number' && Number.isFinite(limit)) {
      next.limit = limit;
    }
  }

  const tokenContext = composeContext(decl, context, form, anchorsBySequence);
  if (Object.keys(tokenContext).length > 0) {
    next.context = tokenContext;
  }

  return next;
}

function composeFilters(
  decl: ReportingModelDataSourceDeclaration,
  context: unknown,
  form: ResolvedForm,
  anchorsBySequence: Readonly<Record<string, AnchorMap>>,
  sequenceByFormKey: ReadonlyMap<string, string>,
): ReportingQueryFilter[] {
  const staticFilters: ReportingQueryFilter[] = [];
  for (const filter of decl.filters ?? []) {
    const resolved = interpolateObject(filter, context) as ReportingQueryFilter;
    if (
      decl.sequenceBindings?.length &&
      readsRoot(filter.value, SEQUENCES_ROOT) &&
      isBlank(resolved.value)
    ) {
      continue;
    }
    staticFilters.push(resolved);
  }
  const occupied = new Set(staticFilters.map((filter) => filter.field));
  const excluded = new Set(
    interpolateStringArray(decl.excludeEqFields ?? [], context),
  );
  const bound: ReportingQueryFilter[] = [];

  for (const entry of form.entries) {
    for (const spec of decl.boundFilters ?? []) {
      if (spec.role !== 'filter' || spec.formKey !== entry.key) continue;
      const filter = filterFromFormValue(spec, entry.value, sequenceByFormKey);
      if (!filter || occupied.has(filter.field)) continue;
      if (filter.op === 'eq' && excluded.has(filter.field)) continue;
      bound.push(filter);
    }
  }

  return rewriteInFilters(
    [...staticFilters, ...bound],
    decl.sequenceBindings,
    anchorsBySequence,
  );
}

function filterFromFormValue(
  spec: ReportingBoundFilter,
  raw: FormStateValue | undefined,
  sequenceByFormKey: ReadonlyMap<string, string>,
): ReportingQueryFilter | undefined {
  if (raw == null || raw === '') return undefined;
  if (Array.isArray(raw)) {
    if (raw.length === 0) return undefined;
    return {
      field: spec.field,
      op: spec.op && LIST_OPS.has(spec.op) ? spec.op : 'in',
      value: raw.map((item) =>
        formatFilterText(spec.formKey, String(item), sequenceByFormKey),
      ),
    };
  }
  if (typeof raw !== 'string') return undefined;
  const value = formatFilterText(spec.formKey, raw, sequenceByFormKey);
  const op = spec.op ?? 'eq';
  return {
    field: spec.field,
    op,
    value: LIST_OPS.has(op) ? [value] : value,
  };
}

function formatFilterText(
  formKey: string,
  value: string,
  sequenceByFormKey: ReadonlyMap<string, string>,
): string {
  const sequenceId = sequenceByFormKey.get(formKey);
  if (!sequenceId) return value;
  const anchors = getSequence(sequenceId)?.anchors(value);
  return anchors?.self ?? value;
}

function rewriteInFilters(
  filters: readonly ReportingQueryFilter[],
  bindings: readonly ReportingSequenceBinding[] | undefined,
  anchorsBySequence: Readonly<Record<string, AnchorMap>>,
): ReportingQueryFilter[] {
  const anchorsByField = new Map<string, AnchorMap>();
  for (const binding of bindings ?? []) {
    if (!binding.rewriteInField) continue;
    const anchors = anchorsBySequence[binding.sequenceId];
    if (!anchors) continue;
    anchorsByField.set(binding.rewriteInField, anchors);
  }
  if (anchorsByField.size === 0) return [...filters];
  return filters.map((filter) => {
    const anchors = anchorsByField.get(filter.field);
    if (!anchors || filter.op !== 'in') return filter;
    return {
      ...filter,
      value: [anchors.self, anchors.prior, anchors.priorPrior].filter(
        (member) => typeof member === 'string' && member.length > 0,
      ),
    };
  });
}

/**
 * Authored context keeps the conversion it has always had (strings and
 * numbers as written, anything else non-null stringified), so a declaration
 * with no bound fields sends the same body and cache key. Bound context
 * tokens follow form-state order; a later key wins a shared field, and a
 * bound token replaces an authored entry of the same name.
 */
function composeContext(
  decl: ReportingModelDataSourceDeclaration,
  context: unknown,
  form: ResolvedForm,
  anchorsBySequence: Readonly<Record<string, AnchorMap>>,
): Record<string, string | number> {
  const tokenContext: Record<string, string | number> = {};
  if (decl.context && Object.keys(decl.context).length > 0) {
    const interpolatedContext = interpolateObject(
      decl.context,
      context,
    ) as Record<string, unknown>;
    for (const [key, value] of Object.entries(interpolatedContext)) {
      if (typeof value === 'string' || typeof value === 'number') {
        tokenContext[key] = value;
      } else if (value != null) {
        tokenContext[key] = String(value);
      }
    }
  }

  for (const binding of decl.sequenceBindings ?? []) {
    const anchors = anchorsBySequence[binding.sequenceId];
    if (!anchors || !binding.contextKeys) continue;
    for (const position of ['self', 'prior', 'priorPrior'] as const) {
      const key = binding.contextKeys[position];
      const value = anchors[position];
      if (key && typeof value === 'string' && value.length > 0) {
        tokenContext[key] = value;
      }
    }
  }

  for (const entry of form.entries) {
    for (const spec of decl.boundFilters ?? []) {
      if (spec.role !== 'context' || spec.formKey !== entry.key) continue;
      const token = firstScalarToken(entry.value);
      if (token === undefined) continue;
      tokenContext[spec.field] = token;
    }
  }
  return tokenContext;
}

/** A scalar, or a list's first item. Empty values contribute nothing. */
function firstScalarToken(raw: FormStateValue | undefined): string | undefined {
  if (raw == null || raw === '') return undefined;
  if (Array.isArray(raw)) {
    return raw.length === 0 ? undefined : String(raw[0]);
  }
  return typeof raw === 'string' ? raw : undefined;
}

/** A scalar, or a list's first non-empty item. */
function firstNonEmptyText(
  raw: FormStateValue | undefined,
): string | undefined {
  if (typeof raw === 'string') return raw.length > 0 ? raw : undefined;
  if (Array.isArray(raw)) {
    for (const item of raw) {
      if (typeof item === 'string' && item.length > 0) return item;
    }
  }
  return undefined;
}

function resolveAllowlist(
  raw: ReportingModelDataSourceDeclaration['applicableFormKeys'],
  context: unknown,
): Allowlist {
  if (raw === undefined || raw === null) return { mode: 'all' };
  if (Array.isArray(raw)) return allowlistFromKeys(raw);
  const resolved = interpolateObject(raw, context);
  if (resolved === null) return { mode: 'all' };
  if (
    Array.isArray(resolved) &&
    resolved.every((item) => typeof item === 'string')
  ) {
    return allowlistFromKeys(resolved as string[]);
  }
  if (!isUnpublishedScope(resolved)) {
    warnOnce(
      'reporting-allowlist',
      `[data-source] applicableFormKeys "${String(raw)}" resolved to neither null nor a list of keys; no form values apply.`,
    );
  }
  return { mode: 'invalid' };
}

function allowlistFromKeys(keys: readonly string[]): Allowlist {
  return keys.length === 0
    ? { mode: 'none' }
    : { mode: 'keys', keys: new Set(keys) };
}

function allows(allow: Allowlist, formKey: string): boolean {
  if (allow.mode === 'all') return true;
  if (allow.mode === 'keys') return allow.keys.has(formKey);
  return false;
}

function resolveForm(
  decl: ReportingModelDataSourceDeclaration,
  formState: FormState,
  allow: Allowlist,
): ResolvedForm {
  const entries: FormEntry[] = [];
  for (const key of Object.keys(formState)) {
    if (allows(allow, key)) entries.push({ key, value: formState[key] });
  }
  const overrides = decl.formOverrides;
  if (overrides) {
    for (const key of Object.keys(overrides)) {
      const value = overrides[key];
      const index = entries.findIndex((entry) => entry.key === key);
      if (value === null) {
        if (index !== -1) entries.splice(index, 1);
      } else if (index !== -1) {
        entries[index] = { key, value };
      } else {
        entries.push({ key, value });
      }
    }
  }
  const byKey: Record<string, FormStateValue> = {};
  for (const entry of entries) byKey[entry.key] = entry.value;
  return { entries, byKey };
}

function resolveSequenceAnchors(
  bindings: readonly ReportingSequenceBinding[] | undefined,
  form: ResolvedForm,
): Record<string, AnchorMap> {
  const anchorsBySequence: Record<string, AnchorMap> = {};
  for (const binding of bindings ?? []) {
    if (
      Object.prototype.hasOwnProperty.call(
        anchorsBySequence,
        binding.sequenceId,
      )
    ) {
      continue;
    }
    const current = firstSequenceCurrent(binding, form);
    if (current === undefined) continue;
    const anchors = getSequence(binding.sequenceId)?.anchors(current) ?? null;
    if (anchors) anchorsBySequence[binding.sequenceId] = anchors;
  }
  return anchorsBySequence;
}

function firstSequenceCurrent(
  binding: ReportingSequenceBinding,
  form: ResolvedForm,
): string | undefined {
  for (const formKey of binding.formKeys) {
    if (!Object.prototype.hasOwnProperty.call(form.byKey, formKey)) continue;
    const token = firstScalarToken(form.byKey[formKey]);
    if (token !== undefined && token.length > 0) return token;
  }
  return undefined;
}

function indexSequencesByFormKey(
  bindings: readonly ReportingSequenceBinding[] | undefined,
): Map<string, string> {
  const index = new Map<string, string>();
  for (const binding of bindings ?? []) {
    for (const formKey of binding.formKeys) {
      if (!index.has(formKey)) index.set(formKey, binding.sequenceId);
    }
  }
  return index;
}

/**
 * The manager has already added the root (see `reportingDerivedRoots`).
 * A direct caller has not, so it is added to a copy here.
 */
function withSequenceRoot(
  decl: ReportingModelDataSourceDeclaration,
  context: unknown,
  anchorsBySequence: Readonly<Record<string, AnchorMap>>,
): unknown {
  if (!decl.sequenceBindings?.length) return context;
  if (isDerivedRoot(context, SEQUENCES_ROOT)) return context;
  return withDerivedRoots(context, { [SEQUENCES_ROOT]: anchorsBySequence });
}

function selectedName(
  selection: ReportingValueSelection,
  form: ResolvedForm,
): string | undefined {
  let current: string | undefined;
  for (const entry of form.entries) {
    if (!selection.formKeys.includes(entry.key)) continue;
    const text = firstNonEmptyText(entry.value);
    if (text !== undefined) current = text;
  }
  if (current === undefined) return undefined;
  if (!Object.prototype.hasOwnProperty.call(selection.valueMap, current)) {
    return undefined;
  }
  const mapped = selection.valueMap[current];
  return typeof mapped === 'string' && mapped.length > 0 ? mapped : undefined;
}

function swapNames(
  selection: ReportingValueSelection,
  form: ResolvedForm,
): ((name: string) => string) | undefined {
  const target = selectedName(selection, form);
  if (target === undefined) return undefined;
  const swappable = new Set(
    Object.keys(selection.valueMap).map((key) => selection.valueMap[key]),
  );
  return (name) => (swappable.has(name) ? target : name);
}

function selectNames(
  names: readonly string[],
  selections: readonly ReportingValueSelection[] | undefined,
  target: ReportingValueSelection['target'],
  form: ResolvedForm,
): readonly string[] {
  let next = names;
  for (const selection of selections ?? []) {
    if (selection.target !== target) continue;
    const swap = swapNames(selection, form);
    if (swap) next = next.map(swap);
  }
  return next;
}

function renameOrder(
  order: readonly ReportingOrderClause[],
  selections: readonly ReportingValueSelection[] | undefined,
  form: ResolvedForm,
): readonly ReportingOrderClause[] {
  let next = order;
  for (const selection of selections ?? []) {
    if (!selection.renameOrder) continue;
    const swap = swapNames(selection, form);
    if (swap) {
      next = next.map((clause) => ({ ...clause, field: swap(clause.field) }));
    }
  }
  return next;
}

function readFormState(context: unknown): FormState {
  if (context === null || typeof context !== 'object') return {};
  const formState = (context as { formState?: unknown }).formState;
  if (
    formState === null ||
    typeof formState !== 'object' ||
    Array.isArray(formState)
  ) {
    return {};
  }
  return formState as FormState;
}

function isBlank(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === 'string') return value.length === 0;
  if (Array.isArray(value)) return value.length === 0 || value.some(isBlank);
  return false;
}

function readsRoot(value: unknown, root: string): boolean {
  if (typeof value === 'string') {
    if (!hasTemplate(value)) return false;
    try {
      return parse(value).segments.some(
        (segment) =>
          segment.type === 'expression' &&
          segment.kind === 'path' &&
          segment.segments[0]?.type === 'key' &&
          segment.segments[0].name === root,
      );
    } catch {
      return false;
    }
  }
  if (Array.isArray(value)) return value.some((item) => readsRoot(item, root));
  return false;
}

function containsUnpublishedScope(
  value: unknown,
  stack = new WeakSet<object>(),
): boolean {
  if (isUnpublishedScope(value)) return true;
  if (typeof value !== 'object' || value === null) return false;
  if (stack.has(value)) return false;
  stack.add(value);
  if (Array.isArray(value)) {
    return value.some((item) => containsUnpublishedScope(item, stack));
  }
  return Object.values(value).some((item) =>
    containsUnpublishedScope(item, stack),
  );
}

function interpolateStringArray(
  values: readonly string[],
  context: unknown,
): readonly string[] {
  return values.map((value) => String(interpolateObject(value, context)));
}
